/**
 * VariantManager.ts - Style variant system
 * 
 * CHANGE HISTORY:
 * - 251102_1653 danielbeer: Created for Phase 3.5a - Inheritance & Variants
 * 
 * Provides:
 * - Variant creation from base styles
 * - Variant templates (size, color, state)
 * - Batch variant generation
 * - Variant naming conventions
 * - Variant metadata tracking
 */

import type { Style, StyleVariables, CreateStyleParams } from '../types';

export interface VariantTemplate {
  name: string;
  description: string;
  variables: Partial<StyleVariables>;
  metadata?: Record<string, any>;
}

export interface VariantConfig {
  baseName?: string; // Override base style name
  namePattern?: string; // Pattern: "{base} - {variant}" or "{variant} {base}"
  gallery?: string;
  metadata?: Record<string, any>;
}

export interface VariantSet {
  base: Style;
  variants: Style[];
  template: string; // Template name used
}

/**
 * Built-in variant templates
 */
export const VARIANT_TEMPLATES = {
  // Size variants
  sizes: {
    small: {
      name: 'Small',
      description: 'Compact size variant',
      variables: {
        '--padding': '8px',
        '--font-size': '12px',
        '--radius': '4px',
        '--spacing': '4px'
      }
    },
    medium: {
      name: 'Medium',
      description: 'Standard size variant',
      variables: {
        '--padding': '16px',
        '--font-size': '14px',
        '--radius': '8px',
        '--spacing': '8px'
      }
    },
    large: {
      name: 'Large',
      description: 'Spacious size variant',
      variables: {
        '--padding': '24px',
        '--font-size': '16px',
        '--radius': '12px',
        '--spacing': '12px'
      }
    }
  },

  // Color variants
  colors: {
    primary: {
      name: 'Primary',
      description: 'Primary brand color',
      variables: {
        '--bg': 'var(--color-primary)',
        '--text': 'var(--color-primary-text)',
        '--border': 'var(--color-primary-border)'
      }
    },
    secondary: {
      name: 'Secondary',
      description: 'Secondary brand color',
      variables: {
        '--bg': 'var(--color-secondary)',
        '--text': 'var(--color-secondary-text)',
        '--border': 'var(--color-secondary-border)'
      }
    },
    success: {
      name: 'Success',
      description: 'Success state color',
      variables: {
        '--bg': 'var(--color-success)',
        '--text': 'var(--color-success-text)',
        '--border': 'var(--color-success-border)'
      }
    },
    danger: {
      name: 'Danger',
      description: 'Danger/error state color',
      variables: {
        '--bg': 'var(--color-danger)',
        '--text': 'var(--color-danger-text)',
        '--border': 'var(--color-danger-border)'
      }
    },
    warning: {
      name: 'Warning',
      description: 'Warning state color',
      variables: {
        '--bg': 'var(--color-warning)',
        '--text': 'var(--color-warning-text)',
        '--border': 'var(--color-warning-border)'
      }
    }
  },

  // State variants
  states: {
    default: {
      name: 'Default',
      description: 'Default state',
      variables: {
        '--opacity': '1',
        '--cursor': 'default'
      }
    },
    hover: {
      name: 'Hover',
      description: 'Hover state',
      variables: {
        '--opacity': '0.9',
        '--cursor': 'pointer',
        '--bg': 'var(--bg-hover)',
        '--border': 'var(--border-hover)'
      }
    },
    active: {
      name: 'Active',
      description: 'Active/pressed state',
      variables: {
        '--opacity': '0.8',
        '--bg': 'var(--bg-active)',
        '--border': 'var(--border-active)'
      }
    },
    disabled: {
      name: 'Disabled',
      description: 'Disabled state',
      variables: {
        '--opacity': '0.5',
        '--cursor': 'not-allowed',
        '--bg': 'var(--bg-disabled)',
        '--text': 'var(--text-disabled)'
      }
    }
  }
};

/**
 * VariantManager - Manages style variants
 */
export class VariantManager {
  private createStyleFn: (params: CreateStyleParams) => Style;

  constructor(createStyleFn: (params: CreateStyleParams) => Style) {
    this.createStyleFn = createStyleFn;
  }

  /**
   * Create a single variant from a base style
   */
  createVariant(
    baseStyle: Style,
    variantName: string,
    variables: Partial<StyleVariables>,
    config: VariantConfig = {}
  ): Style {
    const namePattern = config.namePattern || '{base} - {variant}';
    const name = namePattern
      .replace('{base}', config.baseName || baseStyle.name)
      .replace('{variant}', variantName);

    return this.createStyleFn({
      name,
      type: baseStyle.type,
      extends: baseStyle.id,
      variables: variables as StyleVariables,
      gallery: config.gallery || 'variants',
      metadata: {
        ...config.metadata,
        variant: variantName,
        baseStyle: baseStyle.id,
        isVariant: true
      }
    });
  }

  /**
   * Create multiple variants from templates
   */
  createVariants(
    baseStyle: Style,
    templates: Record<string, VariantTemplate>,
    config: VariantConfig = {}
  ): Style[] {
    return Object.entries(templates).map(([key, template]) => {
      return this.createVariant(
        baseStyle,
        template.name,
        template.variables,
        {
          ...config,
          metadata: {
            ...config.metadata,
            ...template.metadata,
            templateKey: key
          }
        }
      );
    });
  }

  /**
   * Create size variants (small, medium, large)
   */
  createSizeVariants(baseStyle: Style, config: VariantConfig = {}): Style[] {
    return this.createVariants(baseStyle, VARIANT_TEMPLATES.sizes, config);
  }

  /**
   * Create color variants (primary, secondary, success, danger, warning)
   */
  createColorVariants(baseStyle: Style, config: VariantConfig = {}): Style[] {
    return this.createVariants(baseStyle, VARIANT_TEMPLATES.colors, config);
  }

  /**
   * Create state variants (default, hover, active, disabled)
   */
  createStateVariants(baseStyle: Style, config: VariantConfig = {}): Style[] {
    return this.createVariants(baseStyle, VARIANT_TEMPLATES.states, config);
  }

  /**
   * Create a complete variant set (all templates)
   */
  createVariantSet(
    baseStyle: Style,
    templateName: keyof typeof VARIANT_TEMPLATES,
    config: VariantConfig = {}
  ): VariantSet {
    const templates = VARIANT_TEMPLATES[templateName];
    const variants = this.createVariants(baseStyle, templates, config);

    return {
      base: baseStyle,
      variants,
      template: templateName
    };
  }

  /**
   * Create custom variant template
   */
  createCustomVariants(
    baseStyle: Style,
    customTemplates: Record<string, VariantTemplate>,
    config: VariantConfig = {}
  ): Style[] {
    return this.createVariants(baseStyle, customTemplates, config);
  }

  /**
   * Get all variants of a base style
   */
  getVariants(baseStyleId: string, allStyles: Map<string, Style>): Style[] {
    const variants: Style[] = [];
    
    allStyles.forEach(style => {
      if (style.metadata?.baseStyle === baseStyleId && style.metadata?.isVariant) {
        variants.push(style);
      }
    });

    return variants;
  }

  /**
   * Get variants grouped by template
   */
  getVariantsByTemplate(
    baseStyleId: string,
    allStyles: Map<string, Style>
  ): Record<string, Style[]> {
    const variants = this.getVariants(baseStyleId, allStyles);
    const grouped: Record<string, Style[]> = {};

    variants.forEach(variant => {
      const templateKey = variant.metadata?.templateKey || 'custom';
      if (!grouped[templateKey]) {
        grouped[templateKey] = [];
      }
      grouped[templateKey].push(variant);
    });

    return grouped;
  }

  /**
   * Check if a style is a variant
   */
  isVariant(style: Style): boolean {
    return style.metadata?.isVariant === true;
  }

  /**
   * Get base style for a variant
   */
  getBaseStyle(variant: Style, allStyles: Map<string, Style>): Style | null {
    if (!this.isVariant(variant)) return null;
    
    const baseStyleId = variant.metadata?.baseStyle;
    if (!baseStyleId) return null;

    return allStyles.get(baseStyleId) || null;
  }

  /**
   * Clone variant with modifications
   */
  cloneVariant(
    variant: Style,
    newName: string,
    variableOverrides: Partial<StyleVariables> = {}
  ): Style {
    const baseStyleId = variant.metadata?.baseStyle || variant.extends;
    
    return this.createStyleFn({
      name: newName,
      type: variant.type,
      extends: baseStyleId,
      variables: {
        ...variant.variables,
        ...variableOverrides
      } as StyleVariables,
      gallery: variant.gallery,
      metadata: {
        ...variant.metadata,
        clonedFrom: variant.id
      }
    });
  }
}

/**
 * Singleton instance
 */
let variantManagerInstance: VariantManager | null = null;

export function getVariantManager(
  createStyleFn: (params: CreateStyleParams) => Style
): VariantManager {
  if (!variantManagerInstance) {
    variantManagerInstance = new VariantManager(createStyleFn);
  }
  return variantManagerInstance;
}

export function resetVariantManager(): void {
  variantManagerInstance = null;
}
